'use client';

/**
 * ProductSummaryStripCard — compact header strip for the PDP overlay.
 *
 * Slices `FullProductDetail` for name, SKU, list price, and vendor.
 * Designed to mount in the `overlay_header_card` slot of mode4_product
 * (and any future PDP-shaped preset). Shares ONE
 * `graph_product_explore` round-trip with the sibling body cards via
 * React Query dedup on `(strategy, semantic_id)`.
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="product_summary_strip"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas / overlay
 */

import { useMemo } from 'react';
import { Package } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCardExport } from '@/lib/design/card-export';
import { text, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import type { ProductExploreResponse } from '@/lib/api/types';

const PRODUCT_EXPLORE_STRATEGY = 'graph_product_explore';

export function ProductSummaryStripCard() {
  const { entityId, entityType } = useViewportSelection();
  const enabled = !!entityId && entityType === 'product';

  const { data } = useStrategyData<ProductExploreResponse>(
    PRODUCT_EXPLORE_STRATEGY,
    enabled ? { semantic_id: entityId } : {},
    enabled,
  );
  const product = data?.data?.product ?? null;

  // Scene export — overlay (PDP) surface; shared graph_product_explore key.
  useCardExport(useMemo(() => ({
    label: 'Product',
    content_key: 'product_summary_strip',
    surface: 'overlay' as const,
    status: (product ? 'loaded' : enabled ? 'loading' : 'idle') as 'loading' | 'loaded' | 'idle',
    query_key: enabled
      ? ['strategy-data', PRODUCT_EXPLORE_STRATEGY, { semantic_id: entityId }]
      : null,
    text: product ? `${product.name}${product.sku ? ` (${product.sku})` : ''}` : 'Product',
    json: product
      ? {
          name: product.name,
          sku: product.sku ?? null,
          price: product.list_price != null
            ? `$${product.list_price.toLocaleString()}`
            : null,
        }
      : null,
  }), [product, enabled, entityId]));

  if (!product) {
    return (
      <div
        style={{ fontSize: surfaceBase.canvas }}
        className={cn(text.muted, 'flex items-center gap-2 py-1')}
      >
        <Package className="h-4 w-4" />
        <span>Product</span>
      </div>
    );
  }

  const priceLabel =
    product.list_price !== null && product.list_price !== undefined
      ? `$${product.list_price.toLocaleString()}`
      : null;

  return (
    <div
      style={{ fontSize: surfaceBase.canvas }}
      className="flex items-center gap-3 min-w-0 py-1"
    >
      <Package className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className={cn(text.title, 'truncate')}>{product.name}</div>
        <div className={cn(text.muted, 'flex items-center gap-2')}>
          {product.sku && <span className="font-mono truncate">{product.sku}</span>}
          {product.vendor_name && <span className="truncate">· {product.vendor_name}</span>}
        </div>
      </div>
      {priceLabel && <div className={cn(text.value, 'shrink-0')}>{priceLabel}</div>}
    </div>
  );
}
